import React, { useState, useEffect, useRef } from "react";
import { Button, Modal, Typography, message, Statistic, Tooltip } from "antd";
import {
  CreditCardOutlined,
  QuestionCircleOutlined,
  CheckOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { useCallback } from "react";
import facturaApi from "../../modules/admin/facturas/store/api";
import clientBillingApi from "../../modules/client/billing/store/api";

const { Text } = Typography;

const PayAllInvoicesButton = ({
  facturas = [],
  onSuccess,
  onError,
  disabled = false,
  size = "default",
  isClientSide = false,
  className = "",
  clientName = "",
  skipConfirm = false
}) => {
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [buttonState, setButtonState] = useState("initial"); // initial, disabled, confirm, processing, success, error
  const timeoutRef = useRef(null);

  const DEBOUNCE_TIME = 700; // 700ms debounce
  const OUTCOME_DISPLAY_TIME = 700; // 700ms to show success/error

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Calculate totals from filtered facturas
  const calculateTotals = useCallback(() => {
    return facturas.reduce(
      (totals, factura) => {
        const total = parseFloat(factura.total) || 0;
        const paid = parseFloat(factura.totalPaid) || 0;
        const remaining = total - paid;

        // Include unpaid, partial, and overdue invoices
        if (
          factura.status === "unpaid" ||
          factura.status === "partial" ||
          factura.status === "overdue"
        ) {
          totals.totalAmount += total;
          totals.paidAmount += paid;
          totals.remainingAmount += remaining;
          totals.invoiceCount += 1;
        }

        return totals;
      },
      { totalAmount: 0, paidAmount: 0, remainingAmount: 0, invoiceCount: 0 }
    );
  }, [facturas]);

  const totals = calculateTotals();

  const resetToInitial = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setButtonState("initial");
  }, []);

  const handleShowModal = useCallback(() => {
    setModalVisible(true);
  }, []);

  const handleCancel = (e) => {
    if (e) e.stopPropagation();
    setModalVisible(false);
  };

  const handlePayAll = useCallback(
    async (e) => {
      if (e) e.stopPropagation();
      if (loading) return;

      setButtonState("processing");
      setLoading(true);

      try {
        // Get invoice IDs that need to be paid
        const invoiceIds = facturas
          .filter(
            (f) =>
              f.status === "unpaid" || f.status === "partial" || f.status === "overdue"
          )
          .map((f) => f.id);

        const api = isClientSide ? clientBillingApi : facturaApi;
        const result = await api.payAllInvoices(invoiceIds, {
          totalAmount: totals.remainingAmount,
          invoiceCount: totals.invoiceCount
        });

        if (result.success) {
          message.success(`Successfully paid ${totals.invoiceCount} invoice(s)!`);
          setButtonState("success");
          setModalVisible(false);

          // Show success state for 700ms, then trigger success callback
          timeoutRef.current = setTimeout(() => {
            if (onSuccess) {
              onSuccess(result);
            }
            resetToInitial();
          }, OUTCOME_DISPLAY_TIME);
        } else {
          throw new Error("Payment was not successful");
        }
      } catch (error) {
        console.error("Payment error:", error);
        message.error(`Payment failed: ${error.message}`);
        setButtonState("error");

        // Show error state for 700ms, then reset
        timeoutRef.current = setTimeout(() => {
          if (onError) {
            onError(error);
          }
          resetToInitial();
        }, OUTCOME_DISPLAY_TIME);
      } finally {
        setLoading(false);
      }
    },
    [facturas, loading, totals, isClientSide, onSuccess, onError, resetToInitial]
  );

  const handleButtonClick = useCallback(
    (e) => {
      e.stopPropagation();

      if (skipConfirm) {
        // Skip confirm flow - open modal immediately
        handleShowModal();
        return;
      }

      if (buttonState === "initial") {
        // First click: start debounce phase
        setButtonState("disabled");

        timeoutRef.current = setTimeout(() => {
          setButtonState("confirm");
        }, DEBOUNCE_TIME);
      } else if (buttonState === "confirm") {
        // Second click: show modal
        handleShowModal();
        resetToInitial();
      }
      // Ignore clicks in other states
    },
    [buttonState, handleShowModal, resetToInitial, skipConfirm]
  );

  // Don't show button if no invoices need payment (but still show if disabled for admin)
  if (!disabled && (totals.invoiceCount === 0 || totals.remainingAmount <= 0)) {
    return null;
  }

  // Get button properties based on state
  const getButtonProps = () => {
    const baseText = `Pay All Invoices${
      totals.invoiceCount > 0 ? ` (${totals.invoiceCount})` : ""
    }`;

    switch (buttonState) {
      case "initial":
        return {
          type: "primary",
          icon: <CreditCardOutlined />,
          disabled: disabled || totals.invoiceCount === 0,
          style: {
            backgroundColor: disabled ? "#d9d9d9" : "#13c2c2",
            borderColor: disabled ? "#d9d9d9" : "#13c2c2",
            color: disabled ? "#00000040" : "white"
          },
          text: baseText
        };

      case "disabled":
        return {
          type: "default",
          icon: <CreditCardOutlined />,
          disabled: true,
          style: {
            backgroundColor: "#f5f5f5",
            borderColor: "#d9d9d9",
            color: "#bfbfbf"
          },
          text: baseText
        };

      case "confirm":
        return {
          type: "primary",
          icon: <QuestionCircleOutlined />,
          disabled: disabled || totals.invoiceCount === 0,
          style: {
            backgroundColor: "#faad14",
            borderColor: "#faad14",
            color: "white"
          },
          text: "Confirm"
        };

      case "processing":
        return {
          type: "primary",
          icon: <CreditCardOutlined />,
          disabled: true,
          loading: true,
          style: {
            backgroundColor: "#1890ff",
            borderColor: "#1890ff",
            color: "white"
          },
          text: "Processing..."
        };

      case "success":
        return {
          type: "default",
          icon: <CheckOutlined />,
          disabled: true,
          style: {
            backgroundColor: "white",
            borderColor: "#52c41a",
            color: "#52c41a"
          },
          text: "Success"
        };

      case "error":
        return {
          type: "default",
          icon: <CloseOutlined />,
          disabled: true,
          style: {
            backgroundColor: "white",
            borderColor: "#ff4d4f",
            color: "#ff4d4f"
          },
          text: "Failed"
        };

      default:
        return {
          type: "primary",
          icon: <CreditCardOutlined />,
          disabled: disabled || totals.invoiceCount === 0,
          style: {
            backgroundColor: disabled ? "#d9d9d9" : "#13c2c2",
            borderColor: disabled ? "#d9d9d9" : "#13c2c2",
            color: disabled ? "#00000040" : "white"
          },
          text: baseText
        };
    }
  };

  const buttonProps = getButtonProps();

  const buttonElement = (
    <Button
      type={buttonProps.type}
      size={size}
      icon={buttonProps.icon}
      loading={buttonProps.loading}
      disabled={buttonProps.disabled}
      onClick={handleButtonClick}
      className={`pay-all-invoices-button ${className}`}
      style={buttonProps.style}
    >
      {buttonProps.text}
    </Button>
  );

  // Show tooltip when disabled (typically for admin when no client selected)
  if (disabled) {
    return (
      <Tooltip title="Must filter by Client to activate this feature">
        {buttonElement}
      </Tooltip>
    );
  }

  return (
    <>
      {buttonElement}

      <Modal
        title={
          <div>
            Pay All Outstanding Invoices
            {clientName && <Text type="secondary"> - {clientName}</Text>}
          </div>
        }
        open={modalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="pay"
            type="primary"
            loading={loading}
            onClick={handlePayAll}
            style={{
              backgroundColor: "#13c2c2",
              borderColor: "#13c2c2"
            }}
          >
            {loading
              ? "Processing Payment..."
              : `Pay $${totals.remainingAmount.toFixed(2)}`}
          </Button>
        ]}
        width={600}
        zIndex={99999}
        maskClosable={false}
        getContainer={() => document.body}
      >
        <div style={{ marginBottom: 24 }}>
          <Text>
            You are about to pay all outstanding invoices matching the current filters.
            This will process payment using the default payment method.
          </Text>
        </div>

        <div
          style={{
            backgroundColor: "#f5f5f5",
            padding: 16,
            borderRadius: 8,
            marginBottom: 24
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16
            }}
          >
            <Statistic
              title="Invoices to Pay"
              value={totals.invoiceCount}
              suffix="invoice(s)"
            />
            <Statistic
              title="Total Amount"
              value={totals.totalAmount}
              precision={2}
              prefix="$"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16
            }}
          >
            <Statistic
              title="Already Paid"
              value={totals.paidAmount}
              precision={2}
              prefix="$"
            />
            <Statistic
              title="Amount to Charge"
              value={totals.remainingAmount}
              precision={2}
              prefix="$"
              valueStyle={{ color: "#13c2c2", fontWeight: "bold" }}
            />
          </div>
        </div>

        <Text type="warning" style={{ fontSize: "12px" }}>
          <strong>Note:</strong> This action cannot be undone. All payments will be
          processed immediately using the default payment method.
        </Text>
      </Modal>
    </>
  );
};

export default PayAllInvoicesButton;
